import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Hint, Label, Textarea} from "@zendeskgarden/react-forms";
import {Body, Close, Footer, FooterItem, Header, Modal} from "@zendeskgarden/react-modals";
import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {checkAuthorization, userIsAdmin} from "../../authorization/AuthorizationCheckers";
import {Authorizations} from "../../authorization/Authorizations";
import {AuthContext} from "../../contexts/AuthContext";
import BarLoader from "../bar-loader/BarLoader";
import {DriverNote} from "./DriverNoteModal";
import useBulkCustomDriverNoteModal from "./logic/useBulkCustomDriverNoteModal";
import useBulkOpDriverNoteModal from "./logic/useBulkOpDriverNoteModal";
import useBulkWsDriverNoteModal from "./logic/useBulkWsDriverNoteModal";
import * as css from "./styles/driver-note-modal.module.scss";


export interface BulkDriverNoteModalProps {
    driverNames: string[] | null;
    driverNotes: DriverNote[] | null;
    isBusy: boolean;
    onSave: (driverNotes: DriverNote[]) => void;
    onClose: () => void;
}

interface DriverNoteModalDeciderProps {
    lineIds: string[] | null;
    lineNames: string[] | null;
    onSave?: () => void;
    onClose?: () => void;
}

export function WsBulkDriverNoteModal({lineIds, lineNames, onSave, onClose}: DriverNoteModalDeciderProps): ReactElement {
    const {
        driverNoteData,
        isBusy,
        fetchNotes,
        save,
        reset,
    } = useBulkWsDriverNoteModal({
        onSave: onSave,
    });

    useEffect(() => {
        if (lineIds && lineIds.length > 0) {
            fetchNotes(lineIds);
        } else {
            reset();
        }
    }, [lineIds]);

    if (!driverNoteData) {
        return <></>;
    }

    return (
        <BulkDriverNoteModal
            driverNames={lineNames}
            driverNotes={driverNoteData}
            isBusy={isBusy}
            onSave={save}
            onClose={() => {
                reset();
                onClose?.();
            }}
        />
    )
}

export function OpBulkDriverNoteModal({lineIds, lineNames, onSave, onClose}: DriverNoteModalDeciderProps): ReactElement {
    const {
        driverNoteData,
        isBusy,
        fetchNotes,
        save,
        reset,
    } = useBulkOpDriverNoteModal({
        onSave: onSave,
    });

    useEffect(() => {
        if (lineIds && lineIds.length > 0) {
            fetchNotes(lineIds);
        } else {
            reset();
        }
    }, [lineIds]);

    if (!driverNoteData) {
        return <></>;
    }

    return (
        <BulkDriverNoteModal
            driverNames={lineNames}
            driverNotes={driverNoteData}
            isBusy={isBusy}
            onSave={save}
            onClose={() => {
                reset();
                onClose?.();
            }}
        />
    )
}

export function CustomBulkDriverNoteModal({lineIds, lineNames, onSave, onClose}: DriverNoteModalDeciderProps): ReactElement {
    const {
        driverNoteData,
        isBusy,
        fetchNotes,
        save,
        reset,
    } = useBulkCustomDriverNoteModal({
        onSave: onSave,
    });

    useEffect(() => {
        if (lineIds && lineIds.length > 0) {
            fetchNotes(lineIds);
        } else {
            reset();
        }
    }, [lineIds]);

    if (!driverNoteData) {
        return <></>;
    }

    return (
        <BulkDriverNoteModal
            driverNames={lineNames}
            driverNotes={driverNoteData}
            isBusy={isBusy}
            onSave={save}
            onClose={() => {
                reset();
                onClose?.();
            }}
        />
    )
}

export default function BulkDriverNoteModal({driverNames, driverNotes, isBusy, onSave, onClose}: BulkDriverNoteModalProps): ReactElement {
    const {user} = useContext(AuthContext);

    const [localNote, setLocalNote] = useState<string>("");
    const [localIsReadOnly, setLocalIsReadOnly] = useState<boolean>(false);

    useEffect(() => {
        if (driverNotes && driverNotes.length > 0) {
            const allUniqueNoteText = new Set(driverNotes.map(dNote => dNote.note?.trim() ?? ""));
            const allNotesReadOnly = driverNotes.every(dNote => dNote.isReadOnly === true);

            // if every note in the array is the same, then prepopulate the text field with it
            // otherwise, just use an empty string
            if (allUniqueNoteText.size === 1) {
                setLocalNote(Array.from(allUniqueNoteText.values())[0] ?? "");
            } else {
                setLocalNote("");
            }

            // if every note in the array is readOnly, then check the box for readOnly
            setLocalIsReadOnly(allNotesReadOnly);
        }
    }, [driverNotes]);

    const userCanEdit = useMemo<boolean>(() => {
        if (!driverNotes) {
            return false;
        }

        if (!user) {
            return false;
        }
        
        if (userIsAdmin(user)) {
            return true;
        }
        
        const someNotesAreReadOnly = driverNotes.some(dNote => dNote.isReadOnly === true);
        if (someNotesAreReadOnly) {
            return false;
        }

        const someNotesAreValuesLocked = driverNotes.some(dNote => dNote.lineValuesLocked === true);
        if (!checkAuthorization(user, Authorizations.ModelingMethodsAdmin) && someNotesAreValuesLocked) {
            return false;
        }

        return true;
    }, [user, driverNotes]);

    const computedDriverNames = useMemo<string>(() => {
        if (!driverNames) {
            return "";
        }
        const asTruncatedArray = Array.from(driverNames).slice(0, 4);
        let nameString = asTruncatedArray.join(", ");

        if (driverNames.length > 4) {
            nameString += "...";
        }
        return nameString;
    }, [driverNames]);

    return (
        <Modal onClose={onClose} className={css.driverNoteModal}>
            <Header>Update Multiple Driver Notes</Header>
            <Body className={css.modalBody}>
                <BarLoader loading={isBusy} />
                {driverNotes && driverNotes.length > 0 &&
                    <p>Updating notes for {driverNotes.length} drivers, including {computedDriverNames}</p>
                }
                {userCanEdit ?
                    <Field>
                        <Label hidden>Note</Label>
                        <Textarea
                            placeholder="Write anything you would like people to know, regarding these drivers."
                            disabled={isBusy}
                            minRows={2}
                            maxRows={12}
                            value={localNote}
                            onChange={evt => setLocalNote(evt.target.value)}
                        />
                    </Field>
                    :
                    <p className={css.readOnlyNoteText}>{localNote}</p>
                }

                {userIsAdmin(user) &&
                    <Field className={css.readOnlyCheckbox}>
                        <Checkbox
                            disabled={isBusy}
                            checked={localIsReadOnly ?? false}
                            onChange={evt => setLocalIsReadOnly(evt.target.checked)}
                        >
                            <Label>Read-Only</Label>
                            <Hint>Only allow other Admin users to edit or delete these notes.</Hint>
                        </Checkbox>
                    </Field>
                }
            </Body>
            {userCanEdit &&
                <Footer>
                    <FooterItem>
                        <Button
                            isBasic
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </FooterItem>
                    <FooterItem>
                        <Button
                            isPrimary
                            disabled={isBusy}
                            onClick={() => {
                                if (!driverNotes) {
                                    return;
                                }
                                const builtNotesToSave = driverNotes.map(dNote => ({
                                    ...dNote,
                                    note: localNote,
                                    isReadOnly: localIsReadOnly,
                                }));
                                onSave(builtNotesToSave);
                            }}
                        >
                            {isBusy ? "Working..." : "Update Notes"}
                        </Button>
                    </FooterItem>
                </Footer>
            }
            <Close aria-label="Close modal"/>
        </Modal>
    );
}
